import { useEffect, useRef, useState } from 'react';
import doctorService from '../services/doctorService';

/**
 * Horarios de 30 min de un médico para un día (hora de Bogotá), con su estado.
 *
 * Lo comparten el modal de agendar y el de reagendar: así los dos piden lo
 * mismo, descartan igual las respuestas que llegan tarde y muestran los mismos
 * mensajes de error.
 *
 * @param {string}   doctorId
 * @param {string}   date      'YYYY-MM-DD'
 * @param {object}   [options]
 * @param {boolean}  [options.enabled=true]  false no pide nada (modal cerrado)
 * @param {Function} [options.onLoaded]      (slots, respuesta) tras cada carga
 *        correcta; se llama con la versión más reciente, no hace falta memoizarlo.
 */
function useAvailableSlots(doctorId, date, { enabled = true, onLoaded } = {}) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (!enabled || !doctorId || !date) return undefined;

    // Si cambian médico o fecha antes de que llegue la respuesta, la anterior
    // no debe pisar la nueva.
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      setSlots([]);

      try {
        const res = await doctorService.getAvailableSlots(doctorId, date);
        if (cancelled) return;
        const loaded = res.slots || [];
        setSlots(loaded);
        if (!res.available) setError('El médico no atiende este día.');
        onLoadedRef.current?.(loaded, res);
      } catch (err) {
        if (cancelled) return;
        console.error('❌ Error cargando slots disponibles:', err);
        setError('No se pudieron cargar los horarios disponibles.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, doctorId, date]);

  return { slots, loading, error };
}

export default useAvailableSlots;
